import * as c from "@yahshi/contracts";
import type { z } from "zod";
import { createAdminRequest, type AdminHttpOptions } from "../../../lib/adminHttp";
import { M1ApiError } from "../../m1-app/data/M1AppApi";

export interface AnnouncementApi {
  list(query?: Partial<c.AdminAnnouncementListQuery>): Promise<c.AdminAnnouncementListResult>;
  get(id: string): Promise<c.AdminAnnouncement>;
  create(input: c.AdminAnnouncementCreateInput): Promise<c.AdminAnnouncement>;
  update(id: string, input: c.AdminAnnouncementUpdateInput): Promise<c.AdminAnnouncement>;
  publish(id: string, input: c.AdminAnnouncementPublishInput): Promise<c.AdminAnnouncement>;
  withdraw(id: string, input: c.AdminAnnouncementPublishInput): Promise<c.AdminAnnouncement>;
  listPublished(appId: string): Promise<c.PublishedAnnouncement[]>;
  getPublished(appId: string, id: string): Promise<c.PublishedAnnouncement>;
}

const qs = (value: object) => new URLSearchParams(Object.entries(value).map(([key, item]) => [key, String(item)])).toString();

async function readPublished<T>(url: string, schema: z.ZodType<T>, fallback: string): Promise<T> {
  const response = await fetch(url, { headers: { accept: "application/json" } });
  const payload = await response.json() as { success?: boolean; data?: unknown; error?: { code?: string; message?: string } };
  if (!response.ok || !payload.success) throw new M1ApiError(payload.error?.code ?? "ANNOUNCEMENT_NOT_FOUND", payload.error?.message ?? fallback);
  return schema.parse(payload.data);
}

export function createHttpAnnouncementApi(options: AdminHttpOptions): AnnouncementApi {
  const request = createAdminRequest(options);
  const base = options.baseUrl.replace(/\/+$/, "");
  return {
    list: (query = {}) => request(`/admin/announcements?${qs(c.adminAnnouncementListQuerySchema.parse(query))}`, c.adminAnnouncementListResultSchema),
    get: (id) => request(`/admin/announcements/${encodeURIComponent(id)}`, c.adminAnnouncementSchema),
    create: (input) => request("/admin/announcements", c.adminAnnouncementSchema, {
      method: "POST", body: JSON.stringify(c.adminAnnouncementCreateSchema.parse(input)),
    }),
    update: (id, input) => request(`/admin/announcements/${encodeURIComponent(id)}`, c.adminAnnouncementSchema, {
      method: "PATCH", body: JSON.stringify(c.adminAnnouncementUpdateSchema.parse(input)),
    }),
    publish: (id, input) => request(`/admin/announcements/${encodeURIComponent(id)}/publish`, c.adminAnnouncementSchema, {
      method: "POST", body: JSON.stringify(c.adminAnnouncementPublishSchema.parse(input)),
    }),
    withdraw: (id, input) => request(`/admin/announcements/${encodeURIComponent(id)}/withdraw`, c.adminAnnouncementSchema, {
      method: "POST", body: JSON.stringify(c.adminAnnouncementPublishSchema.parse(input)),
    }),
    async listPublished(appId) {
      const listed = await readPublished(
        `${base}/open/apps/${encodeURIComponent(appId)}/announcements`,
        c.publishedAnnouncementListSchema,
        "暂无生效公告",
      );
      return listed.items;
    },
    getPublished: (appId, id) => readPublished(
      `${base}/open/apps/${encodeURIComponent(appId)}/announcements/${encodeURIComponent(id)}`,
      c.publishedAnnouncementSchema,
      "公告不存在或未在生效窗口",
    ),
  };
}
